(() => {
  const announcementSelector = '.qsc2-drawer-announcement .qsc2-announcement';
  const applyClass = 'ulif-qikify-apply';
  const applyLabelClass = 'ulif-qikify-apply__label';
  const applyCheckClass = 'ulif-qikify-apply__check';
  const applyStatusClass = 'ulif-qikify-apply__status';
  const cartStateRequestTimeout = 2000;
  const qikifyCartActionSelector = [
    '.qsc2-sf-discount__action-btn',
    '.qsc2-sf-discount__close-btn',
    '.qsc2-product-item__quantity-btn',
    '.qsc2-product-item__remove-btn',
    '.qsc2-sf-clear-cart__confirm',
    '.qsc2-stickybar__action-btn',
  ].join(', ');
  const qikifyCartStateSelector = [
    '.qsc2-sf-discount__footer',
    '.qsc2-discount__applied-discount',
    '.qsc2-product-item',
    '.qsc2-drawer-footer',
  ].join(', ');
  const selectSelector = '.qsc2-stickybar select.qsc2-variant-opt__select';
  const menuClass = 'ulif-qikify-select-menu';
  const optionClass = 'ulif-qikify-select-option';
  let openMenu = null;
  let openSelect = null;
  let productDataUrl = '';
  let productDataPromise = null;

  const getSelectName = (select) => {
    const label = select.previousElementSibling?.textContent || select.getAttribute('aria-label') || '';
    return label.replace(/:\s*$/, '').trim().toLowerCase();
  };

  const getProductData = () => {
    if (!window.location.pathname.includes('/products/')) return Promise.resolve(null);

    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;

    if (productDataPromise && productDataUrl === url.href) return productDataPromise;

    productDataUrl = url.href;
    productDataPromise = fetch(url.href)
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load product variants (${response.status})`);
        return response.json();
      })
      .catch(() => null);

    return productDataPromise;
  };

  const dispatchSelectChange = (select) => {
    select.dispatchEvent(new Event('change', { bubbles: true }));
    select.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const syncStickyVariantOptions = async () => {
    const selects = [...document.querySelectorAll(selectSelector)];
    const modelSelect = selects.find((select) => getSelectName(select) === 'model');
    const colorSelect = selects.find((select) => ['color', 'colour'].includes(getSelectName(select)));

    if (!modelSelect || !colorSelect) return;

    const productData = await getProductData();
    if (!productData || !modelSelect.isConnected || !colorSelect.isConnected) return;

    const productOptions = Array.isArray(productData.options) ? productData.options : [];
    const modelIndex = productOptions.findIndex((option) => String(option.name).trim().toLowerCase() === 'model');
    const colorIndex = productOptions.findIndex((option) => ['color', 'colour'].includes(String(option.name).trim().toLowerCase()));

    if (modelIndex < 0 || colorIndex < 0 || !Array.isArray(productData.variants)) return;

    const allowedColors = new Set(
      productData.variants
        .filter((variant) => variant.options?.[modelIndex] === modelSelect.value)
        .map((variant) => variant.options?.[colorIndex])
        .filter(Boolean)
    );

    let selectedColorExists = false;
    let firstExistingColor = null;

    for (const option of colorSelect.options) {
      const exists = allowedColors.has(option.value);

      if (option.dataset.ulifOriginallyDisabled === undefined) {
        option.dataset.ulifOriginallyDisabled = String(option.disabled);
      }

      const originallyDisabled = option.dataset.ulifOriginallyDisabled === 'true';
      option.dataset.ulifOptionExists = String(exists);
      option.hidden = !exists;
      option.disabled = !exists || originallyDisabled;

      if (exists && !firstExistingColor) firstExistingColor = option;
      if (exists && option.value === colorSelect.value) selectedColorExists = true;
    }

    if (!selectedColorExists && firstExistingColor) {
      colorSelect.value = firstExistingColor.value;
      dispatchSelectChange(colorSelect);
    }
  };
  let applyStateRefreshTimer = null;
  let applyStateRefreshPromise = null;
  let applyStateRefreshQueued = false;

  const getDiscountCode = (text) => {
    const match = text.match(/\b(?:use|code)\s*:?\s*([A-Z0-9][A-Z0-9_-]{3,})\b/i);
    return match ? match[1] : '';
  };

  const normalizeDiscountCode = (code) => String(code || '').trim().toUpperCase();

  const ensureApplyStatus = (host) => {
    let status = host.querySelector(`.${applyStatusClass}`);
    if (status) return status;

    status = document.createElement('span');
    status.className = applyStatusClass;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    host.append(status);
    return status;
  };

  const setApplyStatus = (apply, message) => {
    const status = apply.parentElement?.querySelector(`.${applyStatusClass}`);
    if (status) status.textContent = message;
  };

  const setApplyState = (apply, code, applied, announce = true) => {
    const wasApplied = apply.dataset.ulifApplied === 'true';

    if (applied) {
      apply.dataset.ulifApplied = 'true';
      apply.setAttribute('aria-disabled', 'true');
      apply.setAttribute('aria-label', `Discount code ${code} is already applied`);
      apply.tabIndex = -1;
    } else {
      delete apply.dataset.ulifApplied;
      apply.removeAttribute('aria-disabled');
      apply.setAttribute('aria-label', `Apply discount code ${code}`);
      apply.removeAttribute('tabindex');
    }

    if (announce && wasApplied !== applied) {
      setApplyStatus(
        apply,
        applied ? `Discount code ${code} applied.` : `Discount code ${code} removed.`
      );
    }
  };

  const refreshApplyStates = () => {
    if (applyStateRefreshPromise) {
      applyStateRefreshQueued = true;
      return applyStateRefreshPromise;
    }

    const request = (async () => {
      const controller = new AbortController();
      let timeoutId;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const cartUrl = new URL(`${root}cart.js`, window.location.origin);
        cartUrl.searchParams.set('_', String(Date.now()));
        const cartRequest = fetch(cartUrl, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        }).then(async (response) => {
          if (!response.ok) return null;
          return response.json();
        });
        const timeout = new Promise((_, reject) => {
          timeoutId = window.setTimeout(() => {
            controller.abort();
            reject(new Error('Timed out fetching Shopify cart state'));
          }, cartStateRequestTimeout);
        });
        const cart = await Promise.race([cartRequest, timeout]);
        if (!cart) return;
        const appliedCodes = new Set(
          (cart.discount_codes || [])
            .filter((discount) => discount.applicable)
            .map((discount) => normalizeDiscountCode(discount.code))
        );

        document.querySelectorAll(`.${applyClass}[data-ulif-discount-code]`).forEach((apply) => {
          const code = apply.dataset.ulifDiscountCode || '';
          setApplyState(apply, code, appliedCodes.has(normalizeDiscountCode(code)));
        });
      } catch (error) {
        // Keep the existing control state when Shopify cart state is temporarily unavailable.
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    applyStateRefreshPromise = request;
    request.finally(() => {
      if (applyStateRefreshPromise === request) {
        applyStateRefreshPromise = null;
      }

      if (applyStateRefreshQueued) {
        applyStateRefreshQueued = false;
        refreshApplyStates();
      }
    });

    return request;
  };

  const scheduleApplyStateRefresh = (delay = 100) => {
    if (applyStateRefreshTimer) return;

    applyStateRefreshTimer = window.setTimeout(() => {
      applyStateRefreshTimer = null;
      refreshApplyStates();
    }, delay);
  };

  const refreshAfterDiscountChange = () => {
    [100, 600, 1400].forEach((delay) => {
      window.setTimeout(() => scheduleApplyStateRefresh(), delay);
    });
  };

  const mutationTouchesQikifyCartState = (mutation) => {
    const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
    if (target?.closest(qikifyCartStateSelector)) return true;

    if (mutation.type !== 'childList') return false;

    return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => (
      node instanceof Element
      && (node.matches(qikifyCartStateSelector) || node.querySelector(qikifyCartStateSelector))
    ));
  };

  const getApplyUrl = (code) => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart`)}`;
  };

  const closeSelectMenu = () => {
    openMenu?.remove();
    openSelect?.closest('.qsc2-select-wrapper')?.removeAttribute('data-ulif-select-open');
    openMenu = null;
    openSelect?.removeAttribute('aria-expanded');
    openSelect = null;
  };

  const positionSelectMenu = (select, menu) => {
    const anchor = select.closest('.qsc2-select-wrapper') || select;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const width = Math.min(rect.width, viewportWidth);
    const menuHeight = Math.min(menu.scrollHeight, Math.min(320, viewportHeight - 24));
    const spaceBelow = viewportHeight - rect.bottom - 12;
    const openAbove = spaceBelow < Math.min(menuHeight, 220) && rect.top > spaceBelow;
    const top = openAbove ? Math.max(12, rect.top - menuHeight - 4) : Math.min(viewportHeight - menuHeight - 12, rect.bottom + 4);
    const left = rect.right <= viewportWidth ? rect.left : Math.max(0, viewportWidth - width);

    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    menu.style.width = `${width}px`;
  };

  const openSelectMenu = async (select) => {
    if (openSelect === select) {
      closeSelectMenu();
      return;
    }

    await syncStickyVariantOptions();
    if (!select.isConnected) return;

    closeSelectMenu();

    const menu = document.createElement('div');
    menu.className = menuClass;
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', select.previousElementSibling?.textContent?.trim() || 'Select an option');

    [...select.options].forEach((option) => {
      if (option.hidden || option.dataset.ulifOptionExists === 'false') return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = optionClass;
      button.textContent = option.textContent;
      button.disabled = option.disabled;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(option.value === select.value));
      button.addEventListener('click', () => {
        select.value = option.value;
        dispatchSelectChange(select);
        closeSelectMenu();
        select.focus({ preventScroll: true });
      });
      menu.append(button);
    });

    document.body.append(menu);
    openMenu = menu;
    openSelect = select;
    select.closest('.qsc2-select-wrapper')?.setAttribute('data-ulif-select-open', 'true');
    select.setAttribute('aria-expanded', 'true');
    positionSelectMenu(select, menu);
    menu.querySelector(`[aria-selected="true"]`)?.focus();
  };

  const enhanceStickySelects = () => {
    document.querySelectorAll(selectSelector).forEach((select) => {
      if (select.dataset.ulifSelectReady === 'true') return;

      select.dataset.ulifSelectReady = 'true';
      select.setAttribute('aria-haspopup', 'listbox');
      select.addEventListener('mousedown', (event) => {
        event.preventDefault();
        openSelectMenu(select);
      });
      select.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openSelectMenu(select);
      });
      select.addEventListener('change', () => {
        window.setTimeout(syncStickyVariantOptions, 0);
      });
    });

    syncStickyVariantOptions();
  };

  const applyInQikifyCart = (code) => {
    const trigger = document.querySelector('.qsc2-side-feature-btn[data-name="discount"]');
    const panel = document.querySelector('.qsc2-sf-content--discount');

    if (!trigger || !panel) return false;

    if (panel.classList.contains('qsc2-sf-content--hide')) {
      trigger.click();
    }

    const input = panel.querySelector('input[aria-label="discount code input"]');
    const saveButton = panel.querySelector('.qsc2-sf-discount__action-btn');
    if (!input || !saveButton) return false;

    const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    valueSetter?.call(input, code);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    saveButton.click();

    return true;
  };

  const enhanceAnnouncements = () => {
    document.querySelectorAll(announcementSelector).forEach((announcement) => {
      const host = announcement.parentElement;
      if (!host) return;

      const code = getDiscountCode(announcement.textContent || '');
      const existingLink = host.querySelector(`.${applyClass}`);

      if (!code) {
        existingLink?.remove();
        host.querySelector(`.${applyStatusClass}`)?.remove();
        return;
      }

      const applyUrl = getApplyUrl(code);
      if (existingLink) {
        ensureApplyStatus(host);
        const codeChanged = existingLink.dataset.ulifDiscountCode !== code;
        existingLink.href = applyUrl;
        existingLink.dataset.ulifDiscountCode = code;
        if (codeChanged) {
          setApplyState(existingLink, code, false, false);
          setApplyStatus(existingLink, '');
          scheduleApplyStateRefresh();
        }
        return;
      }

      const link = document.createElement('a');
      link.className = applyClass;
      link.href = applyUrl;
      link.dataset.ulifDiscountCode = code;
      const label = document.createElement('span');
      label.className = applyLabelClass;
      label.textContent = 'Apply';
      const check = document.createElement('span');
      check.className = applyCheckClass;
      check.dataset.ulifApplyCheck = '';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      link.append(label, check);
      link.setAttribute('aria-label', `Apply discount code ${code}`);
      link.addEventListener('click', (event) => {
        if (link.dataset.ulifApplied === 'true') {
          event.preventDefault();
          return;
        }

        const currentCode = link.dataset.ulifDiscountCode || '';
        if (!currentCode || !applyInQikifyCart(currentCode)) return;
        event.preventDefault();
        refreshAfterDiscountChange();
      });
      host.append(link);
      ensureApplyStatus(host);
      scheduleApplyStateRefresh();
    });
  };

  const enhance = () => {
    enhanceAnnouncements();
    enhanceStickySelects();
  };

  enhance();

  const observer = new MutationObserver((mutations) => {
    enhance();

    if (mutations.some(mutationTouchesQikifyCartState)) {
      scheduleApplyStateRefresh();
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  const retryTimer = window.setInterval(() => {
    enhance();
    if (document.querySelector(selectSelector)) window.clearInterval(retryTimer);
  }, 250);

  window.UlifQikifySelectEnhance = enhanceStickySelects;

  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest(qikifyCartActionSelector)) {
      refreshAfterDiscountChange();
    }

    if (openMenu && event.target instanceof Node && !openMenu.contains(event.target) && event.target !== openSelect) {
      closeSelectMenu();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openMenu) return;
    const currentSelect = openSelect;
    closeSelectMenu();
    currentSelect?.focus({ preventScroll: true });
  });

  window.addEventListener('resize', closeSelectMenu);
  window.addEventListener('scroll', () => {
    if (openMenu && openSelect) positionSelectMenu(openSelect, openMenu);
  }, { passive: true });
})();
